import React, { useMemo, useState } from 'react';
import EntityDropDownFilterContainer from '~/components/entityViewer/entitiesDropDownFilter/EntityDropDownFilterContainer';
import { PurchaseOrderFilterConfigArray, FILTER_QUERY_KEYS } from './PurchaseOrderFilterConfig';
import {
    useStatusOptions,
    useVendorOptions,
    useFacilityOptions,
    useBuyerOptions,
    useExternalSkuSearch,
} from './queries/PurchaseOrderQueries';

// Import shared debounce hook
import { useDebouncedValue } from '~/shared';

/**
 * Encapsulates the filter configuration for the Purchase Orders V2 page.
 *
 * This component:
 * - Fetches filter options from the API (vendors, facilities, statuses, buyers)
 * - Handles remote search for external SKUs with debouncing
 * - Wires up dynamic options to the filter configuration
 * - Passes raw filter values to parent (parent handles transformation)
 *
 * @param {Object} props - Component props.
 * @param {Object} [props.initialValues={}] - Initial filter values (e.g., parsed from URL).
 * @param {(nextFilters: Object) => void} [props.onChange] - Called when applied filters change.
 * @returns {JSX.Element} Filter container with dropdown + chips.
 */
export function PurchaseOrderFilters({ initialValues = {}, onChange }) {
    // --- External SKU remote search state with debouncing ---
    const [externalSkuSearchTerm, setExternalSkuSearchTerm] = useState('');
    const debouncedSkuSearch = useDebouncedValue(externalSkuSearchTerm, 300);

    // Determine if we should fetch external SKU options
    const shouldFetchExternalSkus = typeof debouncedSkuSearch === 'string' && debouncedSkuSearch.trim().length >= 2;

    // --- Fetch filter options from API ---
    const { data: statusOptions = [], isLoading: isLoadingStatuses } = useStatusOptions();
    const { data: vendorOptions = [], isLoading: isLoadingVendors } = useVendorOptions();
    const { data: facilityOptions = [], isLoading: isLoadingFacilities } = useFacilityOptions();
    const { data: buyerOptions = [], isLoading: isLoadingBuyers } = useBuyerOptions();
    const {
        data: externalSkuOptions = [],
        isLoading: isLoadingExternalSkus,
        isError: hasExternalSkuError,
    } = useExternalSkuSearch(debouncedSkuSearch, {
        enabled: shouldFetchExternalSkus,
    });

    /**
     * Derives the effective filter configuration used by the filter container,
     * enriching static config with dynamic remote search options.
     */
    const effectiveFilterConfigs = useMemo(() => {
        return PurchaseOrderFilterConfigArray.map((cfg) => {
            const queryId = cfg.remoteOptions?.queryId;

            // Handle statuses filter
            if (queryId === FILTER_QUERY_KEYS.STATUSES) {
                return {
                    ...cfg,
                    options: statusOptions,
                    menuProps: {
                        ...cfg.menuProps,
                        isLoading: isLoadingStatuses,
                    },
                };
            }

            // Handle vendors filter
            if (queryId === FILTER_QUERY_KEYS.VENDORS) {
                return {
                    ...cfg,
                    options: vendorOptions,
                    menuProps: {
                        ...cfg.menuProps,
                        isLoading: isLoadingVendors,
                    },
                };
            }

            // Handle facilities filter
            if (queryId === FILTER_QUERY_KEYS.FACILITIES) {
                return {
                    ...cfg,
                    options: facilityOptions,
                    menuProps: {
                        ...cfg.menuProps,
                        isLoading: isLoadingFacilities,
                    },
                };
            }

            // Handle buyers filter
            if (queryId === FILTER_QUERY_KEYS.BUYERS) {
                return {
                    ...cfg,
                    options: buyerOptions,
                    menuProps: {
                        ...cfg.menuProps,
                        isLoading: isLoadingBuyers,
                    },
                };
            }

            // Handle external SKU filter (remote search with debouncing)
            if (queryId === FILTER_QUERY_KEYS.EXTERNAL_SKU) {
                const minQueryLength = cfg.menuProps?.minQueryLength ?? 2;
                const showRemotePrompt = (externalSkuSearchTerm || '').length < minQueryLength;

                return {
                    ...cfg,
                    options: externalSkuOptions,
                    menuProps: {
                        ...cfg.menuProps,
                        query: externalSkuSearchTerm,
                        onQueryChange: setExternalSkuSearchTerm,
                        isLoading: isLoadingExternalSkus,
                        hasError: hasExternalSkuError,
                        showRemotePrompt,
                    },
                };
            }

            // Return unchanged config for static filters
            return cfg;
        });
    }, [
        statusOptions,
        isLoadingStatuses,
        vendorOptions,
        isLoadingVendors,
        facilityOptions,
        isLoadingFacilities,
        buyerOptions,
        isLoadingBuyers,
        externalSkuOptions,
        isLoadingExternalSkus,
        hasExternalSkuError,
        externalSkuSearchTerm,
    ]);

    /**
     * Pass raw filter values to parent - parent handles transformation.
     * This avoids double-transformation issues.
     */
    const handleFilterChange = (filterValues) => {
        if (onChange) {
            onChange(filterValues);
        }
    };

    return (
        <EntityDropDownFilterContainer
            title="Purchase Order Filters"
            filterConfigs={effectiveFilterConfigs}
            initialValues={initialValues}
            onChange={handleFilterChange}
            autoApply
        />
    );
}
